import { useState, useEffect, createContext, useContext, useMemo, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { getToday, parseLocalDate } from '../utils';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadSubscription();
    } else {
      setSubscription(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        // Non-critical error - user may not have subscription record yet
      }

      setSubscription(data);
    } catch (err) {
      // Silent failure - subscription state remains null (free tier)
    } finally {
      setLoading(false);
    }
  };

  // Compute isPro based on current subscription state - this updates when subscription changes
  // Pro if: (1) Stripe/Paddle subscription active OR (2) Trial hasn't expired yet
  //
  // NOTE: Trial expiration is checked client-side here for immediate UI feedback, but the
  // backend expire-trials cron job is the source of truth. If a trial has expired but the
  // cron hasn't run yet, the user may briefly retain Pro access until the backend processes it.
  // This design prevents client-side time manipulation from prematurely ending trials.
  const isPro = useMemo(() => {
    if (!subscription) return false;

    // Lifetime accounts are always pro (admin/owner/granted accounts)
    if (subscription.plan === 'lifetime') {
      return true;
    }

    // Active Stripe or Paddle subscription (status is set reliably by the webhook)
    if (subscription.status === 'pro' && (subscription.stripe_subscription_id || subscription.paddle_subscription_id)) {
      return true;
    }

    // Trial access - check if trial_ends_at is in the future
    if (subscription.trial_ends_at) {
      const trialEnd = new Date(subscription.trial_ends_at);
      if (trialEnd > new Date()) {
        return true;
      }
    }

    return false;
  }, [subscription]);

  // Check if user is on a trial (for UI display purposes)
  const isTrialUser = useMemo(() => {
    if (!subscription?.trial_ends_at) return false;
    // Not a trial if they have an active paid subscription (Stripe or Paddle)
    if (subscription.status === 'pro' && (subscription.stripe_subscription_id || subscription.paddle_subscription_id)) return false;
    const trialEnd = new Date(subscription.trial_ends_at);
    return trialEnd > new Date();
  }, [subscription]);

  // Get trial expiration date (for UI display)
  const trialEndsAt = useMemo(() => {
    if (!isTrialUser || !subscription?.trial_ends_at) return null;
    return new Date(subscription.trial_ends_at);
  }, [isTrialUser, subscription]);

  // Compute isDegraded - user had Pro before but is now on free tier
  // This enables "degraded mode" where they can see all data but only use limited items
  const isDegraded = useMemo(() => {
    return !isPro && subscription?.was_pro === true;
  }, [isPro, subscription]);

  // Get user's current limits
  const getLimits = () => {
    if (isPro) {
      return {
        seeds: Infinity,
        plantings: Infinity,
        harvests: Infinity,
        tasks: Infinity,
        layouts: Infinity,
        notifications: true
      };
    }

    return {
      seeds: 100,
      plantings: 100,
      harvests: 100,
      tasks: 100,
      layouts: 1,
      notifications: false
    };
  };

  // Get active item IDs for a given type (used in degraded mode ONLY)
  // Seeds: first 100 by created_at
  // Plantings/Harvests: last 100/100 by created_at
  // Tasks: next 100 upcoming from today
  // Layouts: first 1 by created_at
  const getActiveItemIds = useCallback((items, type) => {
    // All items are active for Pro users OR regular free users (not degraded)
    // Only degraded users (was Pro, now free) have selective active/inactive items
    if (isPro || !isDegraded || !items || items.length === 0) {
      return new Set(items?.map(i => i.id) || []);
    }

    const limits = getLimits();
    const limit = limits[type];

    if (limit === Infinity) {
      return new Set(items.map(i => i.id));
    }

    let activeItems = [];

    if (type === 'seeds' || type === 'layouts') {
      // First N items by created_at (oldest first)
      activeItems = [...items]
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .slice(0, limit);
    } else if (type === 'plantings' || type === 'harvests') {
      // Last N items by created_at (newest first)
      activeItems = [...items]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, limit);
    } else if (type === 'tasks') {
      // Next N upcoming incomplete tasks from today onwards
      const todayStr = getToday();

      activeItems = [...items]
        .filter(t => !t.completed && t.due_date >= todayStr)
        .sort((a, b) => parseLocalDate(a.due_date) - parseLocalDate(b.due_date))
        .slice(0, limit);
    }

    return new Set(activeItems.map(i => i.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, isDegraded]);

  // Check if a specific item is active (not disabled in degraded mode)
  const isItemActive = useCallback((item, activeIds) => {
    // All items are active for Pro users OR regular free users (not degraded)
    if (isPro || !isDegraded) return true;
    return activeIds.has(item.id);
  }, [isPro, isDegraded]);

  // Get only active seeds for dropdowns (excludes disabled seeds in degraded mode)
  const getActiveSeedsForDropdown = useCallback((allSeeds) => {
    // All seeds available for Pro users OR regular free users (not degraded)
    if (isPro || !isDegraded) return allSeeds;

    const activeIds = getActiveItemIds(allSeeds, 'seeds');
    return allSeeds.filter(s => activeIds.has(s.id));
  }, [isPro, isDegraded, getActiveItemIds]);

  // Check if user can perform an action
  const canAdd = async (type, currentCount) => {
    // Pro users can always add
    if (isPro) return { allowed: true };

    // Free and degraded users share the same limits
    const limits = getLimits();
    const limit = limits[type];

    if (currentCount >= limit) {
      const message = isDegraded
        ? `You've reached the limit of ${limit} ${type}. Re-subscribe to Pro for unlimited!`
        : `Free tier limit: ${limit} ${type}. You have ${currentCount}. Upgrade to Pro for unlimited!`;
      return {
        allowed: false,
        reason: message
      };
    }

    return { allowed: true };
  };

  // Refresh subscription (call after payment)
  const refreshSubscription = async () => {
    await loadSubscription();
  };

  const value = {
    subscription,
    loading,
    isPro,  // This is now a memoized value that updates when subscription changes
    isTrialUser, // True if user is on a trial (not Paddle subscription)
    trialEndsAt, // Trial expiration date (Date object or null)
    isDegraded, // True if user had Pro but is now on free tier
    getLimits,
    getActiveItemIds,
    isItemActive,
    getActiveSeedsForDropdown,
    canAdd,
    refreshSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
